import { formatCredits } from "../../constants/credits";

/** @param {unknown} value */
const nonnegativeInteger = (value) =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

/** An absent record belongs to the pre-verification API, never a retroactive pass. */
/** @param {any} verification */
export const touchupVerificationLabel = (verification) =>
  verification?.version === 1 && verification.status === "passed"
    ? "Automated check passed"
    : !verification || (verification.version === null && verification.status === "legacy_unknown")
      ? "Legacy photo — automated check unknown"
      : "Automated check unavailable";

/** Explicit failed/unverified or unsupported evidence never authorizes a new image. */
/** @param {any} photo */
export const isUsableTouchup = (photo) => {
  if (photo?.state !== "done" || typeof photo.edited_url !== "string" || !photo.edited_url)
    return false;
  const verification = photo.verification;
  return (
    !verification ||
    (verification.version === null && verification.status === "legacy_unknown") ||
    (verification.version === 1 && verification.status === "passed")
  );
};

const FAILURE_MESSAGES = {
  verification_unavailable:
    "We couldn't complete the automated check, so this new edit was withheld.",
  verification_invalid: "We couldn't complete the automated check, so this new edit was withheld.",
  fidelity_failed: "This new edit did not pass the property-fidelity check and was withheld.",
  no_edits_verified:
    "The requested improvement could not be verified, so no new edit was delivered.",
  source_missing:
    "The original photo was unavailable. Check the listing's photos before trying again.",
  no_valid_edits: "No supported touch-up could be applied to this photo.",
  generation_failed: "The image service could not create a new edit.",
  generation_unconfirmed:
    "We couldn’t confirm the image service’s result, so no new edit is available.",
  deadline_exceeded:
    "This photo took too long to finish. No new edit is available from this attempt.",
  upload_failed: "The edited photo could not be safely saved.",
  invalid_output_key: "The edited photo could not be safely saved.",
  duplicate_result: "This photo did not return a usable result.",
  missing_result: "This photo did not return a usable result.",
  job_failed: "The touch-up run stopped before this photo could finish.",
};

/** Never render provider/debug text as an owner-facing failure. */
/** @param {unknown} code */
export const touchupFailureMessage = (code) =>
  typeof code === "string" && Object.prototype.hasOwnProperty.call(FAILURE_MESSAGES, code)
    ? FAILURE_MESSAGES[/** @type {keyof typeof FAILURE_MESSAGES} */ (code)]
    : "This attempt could not be verified. No new image is available.";

/** @param {any} settlement */
const validSettlement = (settlement) =>
  !!settlement &&
  nonnegativeInteger(settlement.charged_credits) &&
  nonnegativeInteger(settlement.refunded_credits) &&
  settlement.refunded_credits <= settlement.charged_credits &&
  typeof settlement.free_redo_restored === "boolean";

/** Only affirmative server settlement values support a financial statement. */
/** @param {any} settlement */
export const touchupSettlementMessage = (settlement) => {
  if (!validSettlement(settlement)) {
    return "Refund and free-redo details are not available yet.";
  }
  const parts = [];
  if (settlement.refunded_credits > 0)
    parts.push(`${formatCredits(settlement.refunded_credits)} returned.`);
  if (settlement.free_redo_restored) parts.push("Your free redo was restored.");
  return parts.join(" ");
};

/** The gallery can contain older successes; only this job can describe this run. */
/** @param {any} job @param {string | null | undefined} expectedId */
export const touchupRunSummary = (job, expectedId) => {
  const summary = job?.result_summary;
  if (
    !expectedId ||
    job?.job_id !== expectedId ||
    summary?.version !== 1 ||
    summary.job_id !== expectedId
  )
    return null;
  if (
    !nonnegativeInteger(summary.photos_total) ||
    summary.photos_total < 1 ||
    !nonnegativeInteger(summary.photos_succeeded) ||
    !nonnegativeInteger(summary.photos_failed) ||
    summary.photos_succeeded + summary.photos_failed !== summary.photos_total ||
    !Array.isArray(summary.results) ||
    summary.results.length !== summary.photos_total
  )
    return null;
  const names = new Set();
  let succeeded = 0;
  for (const result of summary.results) {
    if (
      !result ||
      result.job_id !== expectedId ||
      typeof result.photo_filename !== "string" ||
      !result.photo_filename ||
      names.has(result.photo_filename) ||
      !["done", "failed"].includes(result.status)
    )
      return null;
    names.add(result.photo_filename);
    if (result.status === "done") {
      if (result.verification?.version !== 1 || result.verification.status !== "passed")
        return null;
      succeeded += 1;
    }
  }
  if (succeeded !== summary.photos_succeeded) return null;
  return summary;
};

/** @param {any} summary */
export const touchupRunSettlementMessage = (summary) => {
  if (
    !summary ||
    !nonnegativeInteger(summary.refunded_credits) ||
    !nonnegativeInteger(summary.free_redos_restored)
  )
    return "Refund and free-redo details are not available yet.";
  if (
    !Array.isArray(summary.results) ||
    summary.results.some((/** @type {any} */ result) => !validSettlement(result?.settlement))
  )
    return "Refund and free-redo details are not available yet.";
  const refunds = summary.results.map(
    (/** @type {any} */ result) => result.settlement?.refunded_credits
  );
  const restored = summary.results.map(
    (/** @type {any} */ result) => result.settlement?.free_redo_restored
  );
  if (
    refunds.some((/** @type {unknown} */ value) => !nonnegativeInteger(value)) ||
    restored.some((/** @type {unknown} */ value) => typeof value !== "boolean") ||
    refunds.reduce(
      (/** @type {number} */ total, /** @type {number} */ value) => total + value,
      0
    ) !== summary.refunded_credits ||
    restored.filter(Boolean).length !== summary.free_redos_restored
  )
    return "Refund and free-redo details are not available yet.";
  const parts = [];
  if (summary.refunded_credits > 0)
    parts.push(`${formatCredits(summary.refunded_credits)} returned.`);
  if (summary.free_redos_restored > 0)
    parts.push(
      `${summary.free_redos_restored} free redo${summary.free_redos_restored === 1 ? "" : "s"} restored.`
    );
  return parts.join(" ");
};
